import { MediaType } from '@confluence/graphql/entry-points/MediaType.ts';
import { useExperimentalMutation } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalMutation.ts';
import { getOnContentUpdatedObjFromResponse } from '@confluence/graphql/entry-points/sharedTemplateBlueprintReqFns.ts';
import { BadStatusError } from '@confluence/network';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import type {
	UpdateDraftByBlueprintMutationVariables,
	UpdateDraftByBlueprintMutation_experimentalUpdateDraftWithBlueprint_content__links as ContentLinks,
	UpdateDraftByBlueprintMutation_experimentalUpdateDraftWithBlueprint_content_metadata as ContentMetadata,
} from './__types__/UpdateDraftByBlueprintMutation.ts';

export const useExperimentalUpdateDraftWithBlueprintMutation = (): [
	(
		options?:
			| {
					variables?: UpdateDraftByBlueprintMutationVariables | undefined;
					update?:
						| ((
								cache: null,
								result: {
									data?: UpdateDraftByBlueprintResponse | null | undefined;
								},
						  ) => void)
						| undefined;
					optimisticResponse?:
						| UpdateDraftByBlueprintResponse
						| ((vars: UpdateDraftByBlueprintMutationVariables) => UpdateDraftByBlueprintResponse)
						| undefined;
			  }
			| undefined,
	) => Promise<{
		data: UpdateDraftByBlueprintResponse | undefined;
	}>,
	{
		data: UpdateDraftByBlueprintResponse | undefined;
		loading: boolean;
		error: ApolloErrorInterop | undefined;
		called: boolean;
	},
] => {
	return useExperimentalMutation<
		UpdateDraftByBlueprintResponse,
		UpdateDraftByBlueprintMutationVariables,
		UpdateDraftByBlueprintApiResponse
	>({
		url: () =>
			`/wiki/rest/create-dialog/1.0/content/blueprint/instance?expand=space.key,metadata.simple.isFabric`,
		method: 'PUT',
		headers: {
			'Content-Type': MediaType.JSON,
		},
		body: ({ contentId, spaceKey, blueprint, parentPageId, title }) => {
			const blueprintSpec = JSON.parse(blueprint);

			const content: any = {
				id: contentId,
				type: 'page',
				status: 'draft',
				space: {
					key: spaceKey,
				},
				version: {
					number: 1,
				},
			};

			if (title) {
				content.title = title;
			}

			if (parentPageId && parentPageId !== 'no-parent-page') {
				content.ancestors = [{ id: parentPageId }];
			}

			const pageData = {
				content,
				contentBlueprintSpec: {
					blueprintId: blueprintSpec.contentBlueprintId,
					context: blueprintSpec,
				},
			};

			return JSON.stringify(pageData);
		},
		parseResult: (response) => {
			return {
				experimentalUpdateDraftWithBlueprint: getOnContentUpdatedObjFromResponse(response),
			};
		},
		transformError: async (error) => {
			if (error instanceof BadStatusError && error.response?.status === 500) {
				const backendError = await error.response.json();
				return new BadStatusError(backendError.errorMessage, error.response);
			}
			return error;
		},
		maxRetries: 1,
	});
};

interface UpdateDraftByBlueprintResponse {
	experimentalUpdateDraftWithBlueprint: ExperimentalUpdateDraftWithBlueprint | null;
}

export interface ExperimentalUpdateDraftWithBlueprint {
	content: Content | null;
}

interface Content {
	id: string | null;
	type: string | null;
	title: string | null;
	subType: string | null;
	space: ContentSpace | null;
	_links: ContentLinks | null;
	metadata: ContentMetadata | null;
}

interface ContentSpace {
	key: string | null;
}

interface UpdateDraftByBlueprintApiResponse {
	content?: {
		id?: string;
		title?: string;
		subType?: string;
		space?: {
			key?: string;
		};
		_links?: {
			editui?: string;
		};
		metadata?: {
			properties?: {
				editor?: {
					value?: string;
				};
			};
			simple?: {
				isFabric?: boolean;
			};
		};
	};
}
